import React, { useState } from 'react';
import { Plus, Filter, Download, Upload, RefreshCw, X } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { UserList } from './UserList';
import { UserForm } from './UserForm';
import { useUserManagement } from '../../hooks/useUserManagement';
import type { PlatformUser, UserRole, UserStatus, UserFilters, UserBulkAction } from '../../types/user';
import { departments, companies } from '../../data/mockUsers';

interface UserManagementProps {
  currentUser: PlatformUser;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  currentUser
}) => {
  const {
    users,
    selectedUsers,
    filters,
    loading,
    setSelectedUsers,
    setFilters,
    addUser,
    updateUser,
    deleteUser,
    bulkAction,
    resetPassword,
    toggleTwoFactor
  } = useUserManagement();

  const [showUserForm, setShowUserForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PlatformUser | undefined>();

  const handleAddUser = () => {
    setSelectedUser(undefined);
    setShowUserForm(true);
  };

  const handleEditUser = (user: PlatformUser) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };

  const handleFormSubmit = async (formData: Omit<PlatformUser, 'id' | 'createdAt' | 'lastLogin' | 'requirePasswordChange'>) => {
    try {
      if (selectedUser) {
        await updateUser({ id: selectedUser.id, ...formData }, currentUser);
      } else {
        await addUser(formData, currentUser);
      }
      setShowUserForm(false);
    } catch (error) {
      console.error('Failed to save user:', error);
      // In a real app, show error notification
    }
  };

  const handleFormCancel = () => {
    setShowUserForm(false);
    setSelectedUser(undefined);
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    setSelectedUsers(prev => 
      prev.length === users.length ? [] : users.map(u => u.id)
    );
  };

  const handleBulkAction = async (action: UserBulkAction['action'], value: any) => {
    if (selectedUsers.length === 0) return;
    
    try {
      await bulkAction({
        userIds: selectedUsers,
        action,
        value
      }, currentUser);
      setSelectedUsers([]);
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
      // In a real app, show error notification
    }
  };

  const handleToggleStatus = async (userId: string, isDisabled: boolean) => {
    try {
      await updateUser(
        { 
          id: userId, 
          status: isDisabled ? 'inactive' : 'active' 
        } as PlatformUser,
        currentUser
      );
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      // In a real app, show error notification
    }
  };

  const handleFilterChange = (field: keyof UserFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value === 'all' ? undefined : value
    }));
  };

  const handleExportUsers = () => {
    const exportData = users.map(user => ({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      department: user.profile.department?.name || '',
      company: user.profile.company?.name || '',
      lastLogin: new Date(user.lastLogin).toLocaleDateString()
    }));

    const csv = [
      Object.keys(exportData[0]).join(','),
      ...exportData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== undefined && value !== '');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">User Management</h2>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            size="sm"
            onClick={handleExportUsers}
            className="flex items-center gap-2  hover:shadow-md transition-all duration-300 hover:scale-105"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={handleAddUser}  size="sm"
           className="flex items-center gap-2 hover:shadow-md transition-all duration-300 hover:scale-105"
          >
            <Plus className="h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4 h-8">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Filters</span>
          </div>
          <div className="h-8 flex items-center">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="text-gray-500 hover:text-gray-700 transition-opacity duration-200"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>
        
        {/* Single Row Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Search by name, email, department, or company..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full"
            />
          </div>
          <div className="w-[180px]">
            <Select
              value={filters.status?.[0] || 'all'}
              onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : [value as UserStatus])}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[180px]">
            <Select
              value={filters.roles?.[0] || 'all'}
              onValueChange={(value) => handleFilterChange('roles', value === 'all' ? undefined : [value as UserRole])}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[180px]">
            <Select
              value={filters.department || 'all'}
              onValueChange={(value) => handleFilterChange('department', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[180px]">
            <Select
              value={filters.company || 'all'}
              onValueChange={(value) => handleFilterChange('company', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[120px]">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{selectedUsers.length} users selected</span>
            <div className="flex items-center gap-2">
              <Select
                onValueChange={(value) => handleBulkAction('status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Change Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Set Active</SelectItem>
                  <SelectItem value="inactive">Set Inactive</SelectItem>
                  <SelectItem value="suspended">Set Suspended</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleBulkAction('delete', null)}
              >
                Delete Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      <UserList
        users={users}
        selectedUsers={selectedUsers}
        onSelectUser={handleUserSelect}
        onSelectAll={handleSelectAll}
        onEditUser={handleEditUser}
        onDeleteUser={(userId) => deleteUser(userId, currentUser)}
        onResetPassword={(userId) => resetPassword(userId, currentUser)}
        onToggleStatus={handleToggleStatus}
        currentUser={currentUser}
      />

      <Dialog open={showUserForm} onOpenChange={setShowUserForm}>
        <DialogContent className="max-w-[800px] p-6">
          <div className="flex justify-between items-center mb-6">
            <DialogTitle className="text-xl font-semibold">
              {selectedUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </div>
          <UserForm
            user={selectedUser}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}; 